import { DollarSign, TrendingUp, TrendingDown, ArrowDownCircle, ArrowUpCircle, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { trpc } from '@/lib/trpc';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = {
  receitas: '#22c55e',
  despesas: '#ef4444',
  pendentes: '#f59e0b',
  pagas: '#22c55e',
  vencidas: '#ef4444',
  canceladas: '#6b7280',
};

function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  color,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  trend?: 'up' | 'down';
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
          </div>
          <div className={`rounded-full p-3 ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        {trend && (
          <div className="mt-4 flex items-center text-sm">
            {trend === 'up' ? (
              <TrendingUp className="mr-1 h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="mr-1 h-4 w-4 text-red-500" />
            )}
            <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
              {trend === 'up' ? 'Positivo' : 'Negativo'}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { data: resumo, isLoading: resumoLoading } = trpc.dashboard.resumoFinanceiro.useQuery({});
  const { data: fluxoMensal, isLoading: fluxoLoading } = trpc.dashboard.fluxoMensal.useQuery();

  const { data: contasPagar } = trpc.contas.list.useQuery({
    page: 1,
    limit: 5,
    tipo: 'PAGAR',
    status: 'PENDENTE',
  });

  const { data: contasReceber } = trpc.contas.list.useQuery({
    page: 1,
    limit: 5,
    tipo: 'RECEBER',
    status: 'PENDENTE',
  });

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const isVencida = (dataVencimento: string) => {
    return new Date(dataVencimento) < new Date();
  };

  const pieData = resumo
    ? [
        { name: 'Pendentes', value: resumo.contasPorStatus.pendentes, color: COLORS.pendentes },
        { name: 'Pagas', value: resumo.contasPorStatus.pagas, color: COLORS.pagas },
        { name: 'Vencidas', value: resumo.contasPorStatus.vencidas, color: COLORS.vencidas },
        { name: 'Canceladas', value: resumo.contasPorStatus.canceladas, color: COLORS.canceladas },
      ].filter((item) => item.value > 0)
    : [];

  if (resumoLoading || fluxoLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      <div className="grid gap-6 md:grid-cols-3">
        <StatCard
          title="Receitas (Pagas)"
          value={formatCurrency(resumo?.totalReceitas || 0)}
          icon={TrendingUp}
          trend="up"
          color="bg-green-500"
        />
        <StatCard
          title="Despesas (Pagas)"
          value={formatCurrency(resumo?.totalDespesas || 0)}
          icon={TrendingDown}
          trend="down"
          color="bg-red-500"
        />
        <StatCard
          title="Saldo"
          value={formatCurrency(resumo?.saldo || 0)}
          icon={DollarSign}
          trend={(resumo?.saldo || 0) >= 0 ? 'up' : 'down'}
          color="bg-primary-600"
        />
      </div>

      {/* Gráfico de Barras - Receitas x Despesas */}
      <Card>
        <CardHeader>
          <CardTitle>Receitas x Despesas (Últimos 6 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fluxoMensal} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <XAxis dataKey="mes" />
                <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value as number)}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Legend />
                <Bar dataKey="receitas" name="Receitas" fill={COLORS.receitas} radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesas" name="Despesas" fill={COLORS.despesas} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Contas Pendentes */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Contas a Pagar Pendentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowDownCircle className="h-5 w-5 text-red-500" />
                Contas a Pagar
              </div>
              <span className="text-sm font-normal text-gray-500">
                Total: {formatCurrency(resumo?.despesasPendentes || 0)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!contasPagar?.items.length ? (
              <p className="text-gray-500">Nenhuma conta pendente</p>
            ) : (
              <div className="space-y-3">
                {contasPagar.items.map((conta) => (
                  <div
                    key={conta.id}
                    className={`flex items-center justify-between rounded-lg border p-3 ${
                      isVencida(conta.dataVencimento) ? 'border-red-200 bg-red-50' : 'border-gray-100'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{conta.descricao}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span className={isVencida(conta.dataVencimento) ? 'text-red-600 font-medium' : ''}>
                          {formatDate(conta.dataVencimento)}
                          {isVencida(conta.dataVencimento) && ' (Vencida)'}
                        </span>
                      </div>
                    </div>
                    <span className="font-semibold text-red-600">{formatCurrency(conta.valor)}</span>
                  </div>
                ))}
                {contasPagar.meta.total > 5 && (
                  <p className="text-center text-sm text-gray-500">+{contasPagar.meta.total - 5} outras contas</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contas a Receber Pendentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowUpCircle className="h-5 w-5 text-green-500" />
                Contas a Receber
              </div>
              <span className="text-sm font-normal text-gray-500">
                Total: {formatCurrency(resumo?.receitasPendentes || 0)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!contasReceber?.items.length ? (
              <p className="text-gray-500">Nenhuma conta pendente</p>
            ) : (
              <div className="space-y-3">
                {contasReceber.items.map((conta) => (
                  <div
                    key={conta.id}
                    className={`flex items-center justify-between rounded-lg border p-3 ${
                      isVencida(conta.dataVencimento) ? 'border-amber-200 bg-amber-50' : 'border-gray-100'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{conta.descricao}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span className={isVencida(conta.dataVencimento) ? 'text-amber-600 font-medium' : ''}>
                          {formatDate(conta.dataVencimento)}
                          {isVencida(conta.dataVencimento) && ' (Vencida)'}
                        </span>
                      </div>
                    </div>
                    <span className="font-semibold text-green-600">{formatCurrency(conta.valor)}</span>
                  </div>
                ))}
                {contasReceber.meta.total > 5 && (
                  <p className="text-center text-sm text-gray-500">+{contasReceber.meta.total - 5} outras contas</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Gráfico de Pizza - Status das Contas */}
        <Card>
          <CardHeader>
            <CardTitle>Status das Contas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  Nenhuma conta registrada
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        <Card>
          <CardHeader>
            <CardTitle>Resumo Financeiro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Contas Pendentes</span>
                <span className="font-medium text-amber-600">{resumo?.contasPorStatus.pendentes || 0}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Contas Pagas</span>
                <span className="font-medium text-green-600">{resumo?.contasPorStatus.pagas || 0}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Contas Vencidas</span>
                <span className="font-medium text-red-600">{resumo?.contasPorStatus.vencidas || 0}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Contas Canceladas</span>
                <span className="font-medium text-gray-500">{resumo?.contasPorStatus.canceladas || 0}</span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="text-gray-600 font-medium">Receitas Pendentes</span>
                <span className="font-semibold text-green-600">{formatCurrency(resumo?.receitasPendentes || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Despesas Pendentes</span>
                <span className="font-semibold text-red-600">{formatCurrency(resumo?.despesasPendentes || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
